// src/utils/generarContratoCapellanesPDF.js
// Genera el PDF oficial de ALIANZA SOLIDARIA DE CAPELLANES
// usando las páginas del documento original como fondo
// y superponiendo los datos del formulario en las coordenadas exactas.
//
// Las imágenes de fondo están en /public/contrato-capellanes/p1.jpg … p9.jpg
// Se cargan en runtime via fetch para no inflar el bundle JS.

import { jsPDF } from 'jspdf';

// Dimensiones A4 en mm  (210 × 297)
// Imagen original: 800 × 1139 px
// Factor de escala px→mm:  210/800 = 0.2625  |  297/1139 = 0.2607  → usamos A4 exacto
const W = 210;  // mm ancho A4
const H = 297;  // mm alto A4
const PX_W = 800;
const PX_H = 1139;

// Convierte coordenadas en px de la imagen original a mm en el PDF
const px = x => (x / PX_W) * W;
const py = y => (y / PX_H) * H;

// Carga una imagen de fondo y devuelve dataURL
async function cargarPagina(n) {
  const url = `/contrato-capellanes/p${n}.jpg`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`No se pudo cargar la página ${n}`);
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ── Helpers de texto sobre el PDF ────────────────────────────────────────
function txt(doc, text, x, y, opts = {}) {
  const {
    size = 8, bold = false, color = [30, 30, 30],
    align = 'left', maxW = null,
  } = opts;
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  doc.setTextColor(...color);
  const val = text || '';
  if (maxW) {
    const lines = doc.splitTextToSize(val, maxW);
    doc.text(lines, x, y, { align });
  } else {
    doc.text(val, x, y, { align });
  }
}

function checkbox(doc, checked, x, y) {
  doc.setDrawColor(30, 30, 30);
  doc.setLineWidth(0.3);
  doc.rect(x, y - 2.5, 3, 3);
  if (checked) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(26, 77, 46);
    doc.text('✓', x + 0.4, y);
  }
}

function linea(doc, texto, x, y, ancho = 60) {
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.2);
  doc.line(x, y + 0.5, x + ancho, y + 0.5);
  txt(doc, texto, x + 1, y);
}

// ── MAPEO DE CAMPOS POR PÁGINA ────────────────────────────────────────────
// Coordenadas medidas en px sobre la imagen 800×1139.
// Se mapean a mm con px() y py().
// Página 1: encabezado del contrato — no tiene campos variables salvo el N° de contrato
// Página 3: datos personales del asociado
// Página 4: aportes y formato
// Página 9: firmas y fecha

// ── FUNCIÓN PRINCIPAL ─────────────────────────────────────────────────────
export async function generarContratoCapellanesPDF(form, afiliadoId, opcionesGeo = {}, hashContrato = null, onProgress = null) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const fechaHoy = new Date();
  const dia   = String(fechaHoy.getDate()).padStart(2, '0');
  const mes   = fechaHoy.toLocaleDateString('es-CO', { month: 'long' });
  const anio  = fechaHoy.getFullYear();
  const numContrato = afiliadoId ? String(afiliadoId).padStart(6, '0') : '______';

  const fmtFecha = val => {
    if (!val) return '';
    try { return new Date(val + 'T00:00:00').toLocaleDateString('es-CO', { day:'2-digit', month:'long', year:'numeric' }); }
    catch { return val; }
  };

  const ESTADO_CIVIL = { SOLTERO:'Soltero', CASADO:'Casado', DIVORCIADO:'Divorciado', VIUDO:'Viudo', UNION_LIBRE:'Unión libre', OTRO:'Otro' };
  const GENERO = { M:'Masculino', F:'Femenino', NB:'No binario', O:'Otro' };
  const MODALIDAD = { DINERO:'Dinero efectivo / transferencia', TRABAJO:'Trabajo y servicios', BIENES:'Bienes en especie' };

  // ── Cargar y renderizar las 9 páginas ──────────────────────────────────
  for (let n = 1; n <= 9; n++) {
    if (onProgress) onProgress(n, 9);

    if (n > 1) doc.addPage();

    // Fondo: imagen de la página original
    try {
      const imgData = await cargarPagina(n);
      doc.addImage(imgData, 'JPEG', 0, 0, W, H);
    } catch (e) {
      // Si no carga la imagen, página en blanco con aviso
      doc.setFontSize(10);
      doc.setTextColor(200, 0, 0);
      doc.text(`[Imagen de página ${n} no disponible]`, 20, 150);
    }

    // ── DATOS SUPERPUESTOS POR PÁGINA ─────────────────────────────────────

    // ── PÁGINA 1: número de contrato en el encabezado ────────────────────
    if (n === 1) {
      // "No. ___-05-2025" — aparece en el encabezado justo después del título
      txt(doc, `No. ${numContrato}-05-2025`, px(400), py(82), { size: 7.5, bold: true, color:[26,77,46] });
    }

    // ── PÁGINA 3: datos personales ───────────────────────────────────────
    // La pág 3 (índice 2 en base 0) contiene los campos:
    //   Fecha Nacimiento (día/mes/año), Género, País, Ciudad, Departamento,
    //   Código postal, Celular, Email, Código alfa numérico, Estado civil
    if (n === 3) {
      // Fecha de nacimiento
      if (form.fechaNacimiento) {
        const [y2, m2, d2] = form.fechaNacimiento.split('-');
        txt(doc, d2, px(164), py(130), { size: 8 });
        txt(doc, m2, px(230), py(130), { size: 8 });
        txt(doc, y2, px(310), py(130), { size: 8 });
      }

      // Género — checkbox
      const gen = form.genero || 'M';
      txt(doc, GENERO[gen] || gen, px(200), py(158), { size: 8 });

      // País / Ciudad (municipio) / Departamento
      const muni  = opcionesGeo.municipio    || '';
      const depto = opcionesGeo.departamento || '';
      txt(doc, 'Colombia',    px(158), py(186), { size: 8 });
      txt(doc, muni,          px(310), py(186), { size: 8 });
      txt(doc, depto,         px(630), py(186), { size: 8 });
      // Dirección
      txt(doc, form.direccion || '', px(320), py(186), { size: 7.5, maxW: px(300) });

      // Código postal
      txt(doc, form.codigoPostal || '', px(200), py(214), { size: 8 });

      // Celular
      txt(doc, form.celular ? `+57 ${form.celular}` : '', px(128), py(242), { size: 8 });

      // Email
      txt(doc, form.email || '', px(128), py(270), { size: 8 });

      // Código alfa numérico
      txt(doc, form.codigoAlfaNum || '', px(300), py(298), { size: 8 });

      // Estado civil — checkboxes
      const ec = (form.estadoCivil || '').toUpperCase();
      // Posiciones aproximadas de los paréntesis en px
      const ecPos = { SOLTERO:130, CASADO:215, DIVORCIADO:315, VIUDO:430, OTRO:520 };
      Object.entries(ecPos).forEach(([k, xpx]) => {
        checkbox(doc, ec === k, px(xpx), py(326));
      });
    }

    // ── PÁGINA 2: nombres y apellidos (final de pág 2) ───────────────────
    if (n === 2) {
      // Nombre
      txt(doc, form.nombre || '', px(160), py(1050), { size: 8, bold: true });
      // Apellidos
      txt(doc, form.apellidos || '', px(160), py(1078), { size: 8, bold: true });
      // Cédula
      txt(doc, form.cedula || '', px(160), py(1106), { size: 8 });
    }

    // ── PÁGINA 4: beneficiarios ──────────────────────────────────────────
    if (n === 4) {
      const bens = (form.beneficiarios || []).filter(b => b.nombres?.trim());
      const baseY = 410;
      const stepY = 42;
      bens.slice(0, 3).forEach((b, i) => {
        const yy = baseY + i * stepY;
        txt(doc, b.nombres || '',   px(30),  py(yy), { size: 8 });
        txt(doc, b.apellidos || '', px(220), py(yy), { size: 8 });
        txt(doc, b.cedula || '',    px(440), py(yy), { size: 8 });
        txt(doc, b.contacto || '',  px(620), py(yy), { size: 8 });
      });
    }

    // ── PÁGINA 5: modalidad de aporte ────────────────────────────────────
    if (n === 5) {
      const mod = form.modalidadAporte || 'DINERO';
      // Valor de afiliación
      txt(doc, '$75.000', px(440), py(472), { size: 8, bold: true, color:[26,77,46] });

      // Modalidad elegida
      const modPos = { TRABAJO: py(530), BIENES: py(562), DINERO: py(594) };
      if (modPos[mod]) {
        txt(doc, '✓', px(30), modPos[mod], { size: 10, bold: true, color:[26,77,46] });
      }

      // Valor dinero
      if (mod === 'DINERO') {
        txt(doc, '$ 220.000', px(160), py(594), { size: 8 });
      }

      // Primera cuota
      if (form.fechaPrimeraCuota) {
        txt(doc, fmtFecha(form.fechaPrimeraCuota), px(400), py(640), { size: 8 });
      }
    }

    // ── PÁGINA 9: fecha y nombre del asociado en firmas ──────────────────
    if (n === 9) {
      // Fecha: "a los (día) días del Mes de (mes) de 2025"
      txt(doc, dia,  px(620), py(680), { size: 8 });
      txt(doc, mes,  px(660), py(680), { size: 8 });

      // Nombre del asociado bajo la línea de firma derecha
      txt(doc, `${form.nombre || ''} ${form.apellidos || ''}`, px(450), py(790), { size: 8, bold: true });
      txt(doc, `C.C. ${form.cedula || ''}`, px(450), py(805), { size: 7.5 });

      // Hash de integridad al final de la página
      if (hashContrato) {
        doc.setFillColor(235, 245, 235);
        doc.rect(14, py(1060), W - 28, 18, 'F');
        doc.setDrawColor(46, 125, 50);
        doc.setLineWidth(0.3);
        doc.rect(14, py(1060), W - 28, 18);
        txt(doc, 'Huella digital SHA-256 del contrato:', 16, py(1068), { size: 6.5, bold: true, color:[26,77,46] });
        txt(doc, hashContrato, 16, py(1080), { size: 5.5, color:[30,30,30] });
        txt(doc, `No. Solicitud: #${numContrato}  ·  Fecha: ${dia} de ${mes} de ${anio}`, 16, py(1090), { size: 6, color:[100,100,100] });
      }
    }
  }

  return doc;
}

export async function descargarContratoCapellanesPDF(form, afiliadoId, opcionesGeo = {}, hashContrato = null, onProgress = null) {
  const doc = await generarContratoCapellanesPDF(form, afiliadoId, opcionesGeo, hashContrato, onProgress);
  const nombre = `${form.apellidos}_${form.nombre}`.replace(/\s+/g, '_').toUpperCase();
  doc.save(`Contrato_Capellanes_${nombre}_${afiliadoId || 'BORRADOR'}.pdf`);
}
